import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { supabase } from "@/integrations/supabase/client";
import { SiteFooter, SiteNav } from "@/components/SiteChrome";

const searchSchema = z.object({
  next: z.string().optional(),
  mode: z.enum(["signin", "signup"]).optional(),
});

export const Route = createFileRoute("/auth")({
  ssr: false,
  validateSearch: (raw: Record<string, unknown>) => searchSchema.parse(raw),
  beforeLoad: async ({ search }) => {
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      const next = sanitizeNext(search.next) ?? "/admin";
      throw redirect({ href: next });
    }
  },
  head: () => ({
    meta: [
      { title: "Sign in — Akram Fortas" },
      { name: "description", content: "Owner sign-in for Akram Fortas's garden admin." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AuthPage,
});

function sanitizeNext(next: string | undefined): string | null {
  if (!next) return null;
  // Only allow same-origin relative paths.
  if (!next.startsWith("/")) return null;
  if (next.startsWith("//")) return null;
  return next;
}

function AuthPage() {
  const { next, mode: initialMode } = Route.useSearch();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">(initialMode ?? "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) throw error;
        toast.success("Account created. You're signed in.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Signed in.");
      }
      const target = sanitizeNext(next) ?? "/_authenticated/admin";
      // Use href because target may be an OAuth consent URL with query params.
      window.location.href = target;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Something went wrong";
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteNav />
      <main className="container-prose pt-20 pb-16 md:pt-28">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Gardener's gate
        </p>
        <h1 className="mt-6 font-serif text-5xl text-balance text-foreground md:text-6xl">
          {mode === "signup" ? "Plant your key." : "Welcome back."}
        </h1>
        <p className="mt-6 max-w-md text-muted-foreground text-pretty">
          {mode === "signup"
            ? "One-time setup for the site owner. Use the email you'll always sign in with."
            : "Sign in to tend drafts, publish essays, and let authenticated tools reach your notes."}
        </p>

        <form onSubmit={onSubmit} className="mt-12 max-w-md space-y-6">
          <label className="block">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Email
            </span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full border-b hairline bg-transparent py-2 font-serif text-2xl text-foreground focus:border-foreground focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Password
            </span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full border-b hairline bg-transparent py-2 font-serif text-2xl text-foreground focus:border-foreground focus:outline-none"
            />
          </label>
          <div className="flex flex-wrap items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center justify-center rounded-full bg-foreground px-6 py-2.5 text-sm text-background transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "…" : mode === "signup" ? "Create account" : "Sign in"}
            </button>
            <button
              type="button"
              onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
              className="text-sm text-muted-foreground link-quiet underline underline-offset-4"
            >
              {mode === "signup" ? "I already have an account" : "First time here"}
            </button>
            <button
              type="button"
              onClick={() => navigate({ to: "/" })}
              className="ml-auto text-sm text-muted-foreground link-quiet"
            >
              ← Back home
            </button>
          </div>
        </form>
      </main>
      <SiteFooter />
    </div>
  );
}
